import React, { useState, useEffect, lazy, Suspense } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTour } from '../contexts/TourContext'
import { projectsAPI, proposalsAPI, conversationsAPI, invitationsAPI, usersAPI, paymentsAPI } from '../services/api'
import {
  User, LogOut, AlertCircle, Briefcase, Clock, CheckCircle, Users, MessageSquare,
  Star, Euro, Plus, TrendingUp, Calendar, FileText, HelpCircle, Mail,
  ChevronDown, ChevronUp, Eye, ExternalLink, Home, Search, Bell, Settings,
  Headphones, CreditCard, BarChart3, Package, Heart, ArrowLeft
} from 'lucide-react'
import OnboardingTour from '../components/OnboardingTour'
import ProfileCompletionBanner from '../components/ProfileCompletionBanner'
import NotificationBell from '../components/NotificationBell'
import PwaPanel from '../components/PwaPanel'
import InstallBanner from '../components/InstallBanner'

// Lazy load embedded pages
const CreatorsPage = lazy(() => import('./CreatorsPage'))
const MessagesPage = lazy(() => import('./MessagesPage'))
const MyProjectsPage = lazy(() => import('./MyProjectsPage'))
const MyProposalsPage = lazy(() => import('./MyProposalsPage'))
const MyActiveProjectsPage = lazy(() => import('./MyActiveProjectsPage'))
const MyInvitationsPage = lazy(() => import('./MyInvitationsPage'))
const EarningsPage = lazy(() => import('./EarningsPage'))
const AnalyticsPage = lazy(() => import('./AnalyticsPage'))
const ProfilePage = lazy(() => import('./ProfilePage'))
const ProjectsPage = lazy(() => import('./ProjectsPage'))
const ProjectCreatePage = lazy(() => import('./ProjectCreatePage'))
const ProjectDetailPage = lazy(() => import('./ProjectDetailPage'))
const ProjectProposalsPage = lazy(() => import('./ProjectProposalsPage'))
const ProjectWorkspacePage = lazy(() => import('./ProjectWorkspacePage'))
const CreatorDetailPage = lazy(() => import('./CreatorDetailPage'))
const WorkspacesPage = lazy(() => import('./WorkspacesPage'))

const PageLoader = () => (
  <div className="flex items-center justify-center py-20">
    <div className="w-8 h-8 border-2 border-[#3EB489] border-t-transparent rounded-full animate-spin" />
  </div>
)

const DarkDashboardPage = () => {
  const { user, logout } = useAuth()
  const { resetTour } = useTour()
  const navigate = useNavigate()
  const location = useLocation()
  const [stats, setStats] = useState({})
  const [recentActivity, setRecentActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('overview')
  const [settingsTab, setSettingsTab] = useState(null)
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [pendingInvitations, setPendingInvitations] = useState(0)
  const [projectsWithProposals, setProjectsWithProposals] = useState([])
  const [proposalsLoading, setProposalsLoading] = useState(false)
  const [expandedProjects, setExpandedProjects] = useState({})
  const [recommendedCreators, setRecommendedCreators] = useState([])
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [activeView, setActiveView] = useState('home')
  const [creatorProposals, setCreatorProposals] = useState([])
  const [creatorProposalsLoading, setCreatorProposalsLoading] = useState(false)

  // Mobiel: body-scroll blokkeren zolang de sidebar-overlay open staat
  useEffect(() => {
    if (!sidebarOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [sidebarOpen])
  const [selectedProjectId, setSelectedProjectId] = useState(null)
  const [selectedCreatorId, setSelectedCreatorId] = useState(null)
  const [viewHistory, setViewHistory] = useState([])

  // URL-gebaseerde routing — als gebruiker direct naar /project-workspace/:id navigeert,
  // open dan binnen het dashboard (met sidebar) de workspace-view voor dat project.
  // Zelfde geldt voor /projects/:id (detail) en /projects/:id/proposals.
  useEffect(() => {
    const path = location.pathname
    let m
    if ((m = path.match(/^\/project-workspace\/([a-f0-9]{24})$/i))) {
      setActiveView('project-workspace')
      setSelectedProjectId(m[1])
    } else if ((m = path.match(/^\/projects\/([a-f0-9]{24})\/proposals$/i))) {
      setActiveView('project-proposals')
      setSelectedProjectId(m[1])
    } else if ((m = path.match(/^\/projects\/([a-f0-9]{24})$/i))) {
      setActiveView('project-detail')
      setSelectedProjectId(m[1])
    } else if ((m = path.match(/^\/creators\/([a-f0-9]{24})$/i))) {
      setActiveView('creator-detail')
      setSelectedCreatorId(m[1])
    }
  }, [location.pathname])

  // Browser-history sync: het dashboard wisselt views via interne state, dus zonder dit
  // springt de browser-terugknop naar de vorige échte pagina (homepage) i.p.v. één view
  // terug. We pushen per view een history-entry (in switchView) en herstellen de view bij
  // 'popstate' (terugknop). setActiveView remount de view → herstelt o.a. de scrollpositie.
  useEffect(() => {
    // Browser z'n eigen scroll-restoration uitzetten: die zet bij de terugknop async de
    // scroll naar wat hij onthield (0) en racet met onze eigen herstel-logica.
    let prevScrollRestoration
    try { prevScrollRestoration = window.history.scrollRestoration; window.history.scrollRestoration = 'manual' } catch (e) {}
    try { window.history.replaceState({ ugcView: activeView, projectId: selectedProjectId, creatorId: selectedCreatorId }, '') } catch (e) {}
    const onPop = (e) => {
      const s = e.state
      if (s && s.ugcView) {
        setActiveView(s.ugcView)
        setSelectedProjectId(s.projectId || null)
        setSelectedCreatorId(s.creatorId || null)
        setSidebarOpen(false)
      }
    }
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      try { if (prevScrollRestoration) window.history.scrollRestoration = prevScrollRestoration } catch (e) {}
    }
  }, [])

  const isCompany = user?.role === 'bedrijf' || user?.role === 'agency'

  // Huisstijl kleuren: groen voor zowel bedrijven als creators (consistent met homepage)
  const accent = '#3EB489'
  const accentHover = '#359E78'
  const accentBg = 'bg-[#3EB489]'
  const accentText = 'text-[#3EB489]'
  const accentBgLight = 'bg-emerald-50'
  const accentBorder = 'border-[#3EB489]/30'

  // settingsTab: laat een aanroeper het tabblad kiezen waarop Instellingen opent,
  // bijv. Profiel voor de bedrijfsnaam of Facturatie voor het KvK-veld.
  const switchView = (view, id = null, tab = null) => {
    // Creators mogen geen andere creator profielen bekijken
    if (view === 'creator-detail' && user?.role === 'creator') return
    setSettingsTab(view === 'settings' ? tab : null)
    // Push current state to history for back navigation
    setViewHistory(prev => [...prev, { view: activeView, projectId: selectedProjectId, creatorId: selectedCreatorId, tab: activeTab }])
    setActiveView(view)
    setSidebarOpen(false)
    if (view === 'home') setActiveTab('overview')
    if (view === 'voorstellen') {
      setActiveTab('voorstellen')
      // mobiel: de tabbalk staat onder banners/waarschuwingen buiten beeld, anders lijkt de klik niets te doen
      setTimeout(() => { const el = document.getElementById('dashboard-tabs'); if (el && window.innerWidth < 1024) el.scrollIntoView({ behavior: 'smooth', block: 'start' }) }, 80)
    }
    if (['project-detail', 'project-proposals', 'project-workspace', 'create-project'].includes(view)) {
      setSelectedProjectId(id)
    }
    if (view === 'creator-detail') {
      setSelectedCreatorId(id)
    }
    // Nieuwe history-entry zodat de browser-terugknop één view terug gaat
    const nextProjectId = ['project-detail', 'project-proposals', 'project-workspace', 'create-project'].includes(view) ? id : selectedProjectId
    const nextCreatorId = view === 'creator-detail' ? id : selectedCreatorId
    try { window.history.pushState({ ugcView: view, projectId: nextProjectId, creatorId: nextCreatorId }, '') } catch (e) {}
  }

  const goBack = () => {
    if (viewHistory.length > 0) {
      const prev = viewHistory[viewHistory.length - 1]
      setViewHistory(h => h.slice(0, -1))
      setActiveView(prev.view)
      setSelectedProjectId(prev.projectId)
      setSelectedCreatorId(prev.creatorId)
      if (prev.tab) setActiveTab(prev.tab)
    } else {
      setActiveView('home')
      setActiveTab('overview')
    }
  }

  useEffect(() => {
    fetchDashboardData()
    fetchUnreadMessages()
    if (user?.role === 'creator') {
      fetchPendingInvitations()
      fetchCreatorProposals()
    }
    if (isCompany) fetchRecommendedCreators()

    const intervalId = setInterval(() => {
      fetchDashboardData(true) // stil verversen — geen paginabrede loader/remount
      fetchUnreadMessages()
      if (user?.role === 'creator') fetchPendingInvitations()
    }, 30000)

    return () => clearInterval(intervalId)
  }, [])

  const fetchRecommendedCreators = async () => {
    try {
      const response = await usersAPI.getCreators({ limit: 4, sort: 'rating' })
      setRecommendedCreators(response.data.creators || [])
    } catch (err) {
      console.error('Error fetching creators:', err)
    }
  }

  const fetchCreatorProposals = async () => {
    try {
      setCreatorProposalsLoading(true)
      const response = await proposalsAPI.getMyProposals()
      setCreatorProposals(response.data.proposals || [])
    } catch (err) {
      console.error('Error fetching creator proposals:', err)
    } finally {
      setCreatorProposalsLoading(false)
    }
  }

  // silent=true bij de 30s-achtergrondrefresh: dan NIET de paginabrede `loading`-loader
  // tonen. Anders unmount/remount het hele dashboard (incl. een half-ingevuld formulier)
  // elke 30 seconden — dat veroorzaakte de "pagina herlaadt en mijn invoer is weg"-bug.
  const fetchDashboardData = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      if (isCompany) {
        const projectsResponse = await projectsAPI.getMyProjects()
        const projects = projectsResponse.data.projects || []
        // Meerdere creators per opdracht: /projects/my geeft alleen opdrachten
        // (geen kindprojecten) terug, met per opdracht `children`. Een opdracht
        // telt als actief als hij open staat of als er een creator aan het werk is.
        const LOPEND = ['proposal_accepted', 'creator_selected', 'awaiting_product_delivery', 'in_progress', 'delivered', 'under_review', 'revision_requested', 'approved']
        // Workspaces waar het bedrijf iets moet doen (aanbetaling, beoordeling, eindbetaling)
        const ACTIE = ['proposal_accepted', 'delivered', 'under_review', 'approved']
        const werkruimtes = projects.flatMap(p => (p.children && p.children.length) ? p.children : (p.selectedCreator ? [p] : []))
        setStats({
          totalProjects: projects.length,
          activeWorkspaces: werkruimtes.filter(w => ACTIE.includes(w.status)).length,
          activeProjects: projects.filter(p => p.status === 'open' || (p.children || []).some(c => LOPEND.includes(c.status))).length,
          completedProjects: projects.filter(p => p.status === 'completed').length,
          totalProposals: projects.reduce((sum, p) => sum + (p.stats?.proposalsCount || 0), 0)
        })
        setRecentActivity(projects.slice(0, 5).map(project => ({
          type: 'project', title: project.title, status: project.status,
          date: project.createdAt || project.publishedAt || project.deadline, data: { ...project, proposalCount: project.stats?.proposalsCount || 0 }
        })))
      } else {
        try {
          const proposalsResponse = await proposalsAPI.getMyProposals()
          const proposals = proposalsResponse.data.proposals || []
          setStats({
            totalProposals: proposals.length,
            acceptedProposals: proposals.filter(p => p.status === 'accepted').length,
            completedProjects: proposals.filter(p => p.status === 'completed').length,
            totalEarnings: proposals.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.agreedPrice || 0), 0)
          })
          setRecentActivity(proposals.slice(0, 5).map(proposal => ({
            type: 'proposal', title: proposal.projectId?.title || 'Untitled Project',
            status: proposal.status, date: proposal.createdAt, data: proposal
          })))
        } catch (err) {
          setStats({ totalProposals: 0, acceptedProposals: 0, completedProjects: 0, totalEarnings: 0 })
          setRecentActivity([])
        }
      }
    } catch (error) {
      if (!silent) setError('Er is een fout opgetreden bij het laden van je dashboard')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const fetchUnreadMessages = async () => {
    try {
      const response = await conversationsAPI.getConversations({ limit: 100 })
      const conversations = response.data.conversations || []
      const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0)
      setUnreadMessages(totalUnread)
      document.title = totalUnread > 0 ? `(${totalUnread}) Dashboard - UGC4you` : 'Dashboard - UGC4you'
    } catch (error) {}
  }

  const fetchPendingInvitations = async () => {
    try {
      const response = await invitationsAPI.getPendingCount()
      setPendingInvitations(response.data.count || 0)
    } catch (error) {}
  }

  useEffect(() => {
    if (activeTab === 'voorstellen' && isCompany && projectsWithProposals.length === 0) {
      fetchProjectsWithProposals()
    }
  }, [activeTab])

  const fetchProjectsWithProposals = async () => {
    try {
      setProposalsLoading(true)
      const projectsResponse = await projectsAPI.getMyProjects()
      const projects = projectsResponse.data.projects || []
      const projectsData = await Promise.all(
        projects.map(async (project) => {
          try {
            const proposalsResponse = await proposalsAPI.getProjectProposals(project._id || project.id)
            return { ...project, proposals: proposalsResponse.data.proposals || [] }
          } catch (err) { return { ...project, proposals: [] } }
        })
      )
      setProjectsWithProposals(projectsData.filter(p => p.proposals.length > 0))
    } catch (error) {} finally { setProposalsLoading(false) }
  }

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }))
  }

  // Accepteer voorstel + start direct de aanbetaling (zelfde flow als ProjectProposalsPage).
  // Zonder deze betaalstap strandt het bedrijf: accepteren zet status op 'proposal_accepted',
  // maar de workspace opent pas na de aanbetaling.
  const handleAcceptAndPay = async (proposal, project) => {
    const budget = proposal.finalPrice || proposal.customPricing?.amount || proposal.proposedPrice || project?.budget || 0
    const depositPercent = project?.payment?.depositPercent ?? 10
    const restPercent = 100 - depositPercent
    const depositIncBtw = (budget * (depositPercent / 100) * 1.21).toFixed(2)

    const confirmed = window.confirm(
      `Je legt deze creator vast en gaat het voorstel accepteren.\n\n` +
      `Om de workspace te openen betaal je nu een aanbetaling van ${depositPercent}% ` +
      `(€${depositIncBtw} incl. BTW). De resterende ${restPercent}% reken je af na goedkeuring van de content.\n\n` +
      `Je kunt daarna nog meer creators voor deze opdracht kiezen; elke creator heeft een eigen aanbetaling en workspace.\n\n` +
      `🔒 Veilig: je aanbetaling staat vast in escrow en gaat pas naar de creator nadat jij de content hebt goedgekeurd.\n\n` +
      `Doorgaan naar aanbetaling?`
    )
    if (!confirmed) return

    try {
      const acceptResp = await proposalsAPI.acceptProposal(proposal.id)
      const projectId = acceptResp?.data?.project?.id || project?._id || project?.id

      const checkoutResp = await paymentsAPI.processDepositPayment(projectId)
      const checkoutUrl = checkoutResp?.data?.data?.checkoutUrl || checkoutResp?.data?.checkoutUrl

      if (checkoutUrl) {
        window.location.href = checkoutUrl
        return
      }

      // Fallback als checkout niet beschikbaar is
      alert('Voorstel geaccepteerd. Ga naar je opdracht om de aanbetaling te voldoen.')
      fetchProjectsWithProposals()
    } catch (error) {
      console.error('Error accepting proposal:', error)
      const msg = error?.response?.data?.message || error?.response?.data?.error || 'Er is een fout opgetreden bij het accepteren van het voorstel'
      alert(msg)
    }
  }

  const handleRejectProposal = async (proposalId) => {
    try {
      await proposalsAPI.rejectProposal(proposalId)
      fetchProjectsWithProposals()
    } catch (error) {
      console.error('Error rejecting proposal:', error)
      const msg = error?.response?.data?.message || error?.response?.data?.error || 'Er is een fout opgetreden bij het afwijzen van het voorstel'
      alert(msg)
    }
  }

  const handleLogout = () => { logout(); navigate('/') }

  const getStatusColor = (status) => {
    const colors = {
      draft: 'text-gray-500 bg-gray-100', open: 'text-[#3EB489] bg-emerald-50',
      in_progress: 'text-yellow-600 bg-yellow-50', in_review: 'text-[#7c3aed] bg-purple-50',
      completed: 'text-emerald-600 bg-emerald-50', cancelled: 'text-red-500 bg-red-50',
      pending: 'text-yellow-600 bg-yellow-50', accepted: 'text-emerald-600 bg-emerald-50',
      rejected: 'text-red-500 bg-red-50', awaiting_deposit: 'text-orange-600 bg-orange-50',
      proposal_accepted: 'text-orange-600 bg-orange-50', awaiting_product_delivery: 'text-[#1D8BF8] bg-blue-50',
      delivered: 'text-[#7c3aed] bg-purple-50', under_review: 'text-[#7c3aed] bg-purple-50',
      revision_requested: 'text-orange-600 bg-orange-50', approved: 'text-emerald-600 bg-emerald-50'
    }
    return colors[status] || 'text-gray-500 bg-gray-100'
  }

  const getStatusText = (status) => {
    const texts = {
      draft: 'Concept', open: 'Open', in_progress: 'Bezig', in_review: 'In beoordeling',
      completed: 'Voltooid', cancelled: 'Geannuleerd', pending: 'In afwachting',
      accepted: 'Geaccepteerd', rejected: 'Afgewezen', awaiting_deposit: 'Wacht op betaling',
      proposal_accepted: 'Wacht op aanbetaling', awaiting_product_delivery: 'Product onderweg',
      delivered: 'Opgeleverd', under_review: 'In beoordeling',
      revision_requested: 'Revisie gevraagd', approved: 'Goedgekeurd'
    }
    return texts[status] || status
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Goedemorgen'
    if (hour < 18) return 'Goedemiddag'
    return 'Goedenavond'
  }

  const displayName = user?.role === 'creator'
    ? user?.firstName
    : (user?.contactPerson || user?.agencyName || user?.companyName)

  if (loading) {
    return (
      <div className="font-['Inter'] min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-8 h-8 border-2 border-[#3EB489] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // Sidebar navigation items - all use switchView to stay in dashboard
  // Emojis ipv Lucide icons voor consistency met homepage-preview
  const sidebarItems = isCompany ? [
    { emoji: '🏠', label: 'Home', view: 'home' },
    { emoji: '👥', label: 'Vind Creators', view: 'creators' },
    { emoji: '💬', label: 'Berichten', view: 'messages', badge: unreadMessages || null },
    { emoji: '🔔', label: 'Voorstellen', view: 'voorstellen', badge: stats.totalProposals || null },
    { emoji: '📋', label: 'Mijn Opdrachten', view: 'my-projects' },
    { emoji: '🎬', label: 'Workspaces', view: 'workspaces', badge: stats.activeWorkspaces || null },
    { emoji: '📊', label: 'Analytics', view: 'analytics' },
    { emoji: '⚙️', label: 'Facturen & Instellingen', view: 'settings' },
  ] : [
    { emoji: '🏠', label: 'Home', view: 'home' },
    { emoji: '🔍', label: 'Zoek Opdrachten', view: 'projects' },
    { emoji: '💬', label: 'Berichten', view: 'messages', badge: unreadMessages || null },
    { emoji: '📩', label: 'Uitnodigingen', view: 'invitations', badge: pendingInvitations || null },
    { emoji: '📋', label: 'Mijn Opdrachten', view: 'active-projects' },
    { emoji: '📄', label: 'Mijn Voorstellen', view: 'my-proposals' },
    { emoji: '💰', label: 'Inkomsten', view: 'earnings' },
  ]

  const isHomeView = activeView === 'home' || activeView === 'voorstellen'

  return (
    <div className="font-['Inter'] min-h-screen flex bg-[#F8FAFC] text-[#0F172A]">
      <InstallBanner />
      <OnboardingTour />

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen supports-[height:100dvh]:h-[100dvh] w-[260px] bg-white border-r border-gray-200 flex flex-col z-50 transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="px-6 py-5 flex items-center justify-between gap-2">
          <Link to="/" className="text-xl font-black tracking-tighter text-[#0F172A]">
            UGC<span style={{ color: accent }}>4you</span>
          </Link>
          <div className="hidden lg:block">
            <NotificationBell accent={accent} onNavigate={switchView} />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 overflow-y-auto">
          {sidebarItems.map((item, i) => {
            const isActive = activeView === item.view

            return (
              <button
                key={i}
                data-tour={`sidebar-${item.view}`}
                onClick={() => {
                  switchView(item.view)
                  if (item.view === 'voorstellen') setActiveTab('voorstellen')
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-1 text-sm transition text-left ${
                  isActive
                    ? `${accentBgLight} font-semibold`
                    : 'text-[#475569] hover:bg-gray-50'
                }`}
                style={isActive ? { color: accent } : {}}
              >
                <span className="text-lg leading-none w-5 flex-shrink-0" aria-hidden="true">{item.emoji}</span>
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span className="ml-auto text-white text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: accent }}>
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            )
          })}

          {/* Matchmaking Card - only for companies */}
          {isCompany && (
            <div className={`mt-6 mx-1 p-4 ${accentBgLight} rounded-2xl text-center`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: accent }}>
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-sm font-bold text-[#0F172A] mb-1">UGC Matchmaking</h4>
              <p className="text-xs text-[#475569] mb-3 leading-relaxed">
                Laat ons de perfecte creator(s) voor jouw campagne vinden.
              </p>
              <button onClick={() => switchView('creators')} className="inline-block w-full py-2 rounded-xl text-xs font-semibold transition" style={{ border: `1px solid ${accent}`, color: accent }} onMouseEnter={e => { e.target.style.backgroundColor = accent; e.target.style.color = 'white' }} onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = accent }}>
                Bekijk Creators
              </button>
            </div>
          )}

          {/* Opdracht Finder Card - only for creators */}
          {!isCompany && (
            <div className={`mt-6 mx-1 p-4 ${accentBgLight} rounded-2xl text-center`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: accent }}>
                <Search className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-sm font-bold text-[#0F172A] mb-1">Opdracht Finder</h4>
              <p className="text-xs text-[#475569] mb-3 leading-relaxed">
                Ontdek nieuwe opdrachten die bij jouw niche passen.
              </p>
              <button onClick={() => switchView('projects')} className="inline-block w-full py-2 rounded-xl text-xs font-semibold transition" style={{ border: `1px solid ${accent}`, color: accent }} onMouseEnter={e => { e.target.style.backgroundColor = accent; e.target.style.color = 'white' }} onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = accent }}>
                Zoek Opdrachten
              </button>
            </div>
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="border-t border-gray-200 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <button onClick={() => switchView('settings')} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#475569] hover:bg-gray-50 transition text-left">
            <Settings className="w-[18px] h-[18px]" />
            <span>Instellingen</span>
          </button>
          <PwaPanel />
          <button onClick={() => { setSidebarOpen(false); resetTour() }} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#475569] hover:bg-gray-50 transition">
            <Headphones className="w-[18px] h-[18px]" />
            <span>Help</span>
          </button>
          <div className="flex items-center gap-3 px-4 py-3 mt-1">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent }}>
              {user?.profilePictureUrl ? (
                <img src={user.profilePictureUrl} alt="" className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <span className="text-white text-sm font-bold">{displayName?.charAt(0) || 'U'}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0F172A] truncate">{displayName}</p>
              <p className="text-[11px] text-[#475569] truncate">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="p-2.5 -mr-2 rounded-lg text-[#94A3B8] hover:text-red-500 hover:bg-gray-50 transition" title="Uitloggen" aria-label="Uitloggen">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 min-w-0 w-0 flex flex-col ${isHomeView ? '' : 'h-screen supports-[height:100dvh]:h-[100dvh]'}`}>
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-30 shrink-0 bg-white/95 backdrop-blur-xl border-b border-gray-100 px-4 py-2 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="p-2.5 hover:bg-gray-100 rounded-lg" aria-label="Menu openen">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <Link to="/" className="text-lg font-black tracking-tighter text-[#0F172A]">
            UGC<span style={{ color: accent }}>4you</span>
          </Link>
          <div className="flex items-center gap-2">
            <NotificationBell accent={accent} onNavigate={switchView} />
            <button onClick={() => switchView('messages')} data-tour="mobile-messages" className="p-2.5 hover:bg-gray-100 rounded-lg relative" aria-label="Berichten">
              <MessageSquare className="w-5 h-5 text-[#475569]" />
              {unreadMessages > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">{unreadMessages}</span>}
            </button>
          </div>
        </div>

        {/* Home: document scrolt. Embedded views: deze div vult de resthoogte van <main> en scrolt intern
            (geen vaste 56px-aftrek meer; overflow-x-hidden als vangnet tegen horizontale scroll). */}
        <div id="dashboard-scroll" className={`${isHomeView ? 'px-4 md:px-8 lg:px-12 py-6 md:py-8' : 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden'}`}>
          {/* Embedded page views - full width */}
          {!isHomeView && (
            <Suspense fallback={<PageLoader />}>
              <div className="embedded-page h-full [&>div]:min-h-full [&>div]:max-w-none [&>div]:mx-0 [&_.max-w-7xl]:max-w-none [&_.max-w-6xl]:max-w-none [&_.max-w-5xl]:max-w-none [&_.max-w-4xl]:max-w-none" style={{ '--hide-page-header': '1' }}>
                {activeView === 'creators' && <CreatorsPage onNavigate={switchView} />}
                {activeView === 'messages' && <MessagesPage embedded />}
                {activeView === 'my-projects' && <MyProjectsPage onNavigate={switchView} />}
                {activeView === 'workspaces' && <WorkspacesPage onNavigate={switchView} />}
                {activeView === 'analytics' && <AnalyticsPage />}
                {activeView === 'settings' && <ProfilePage initialTab={settingsTab} />}
                {activeView === 'projects' && <ProjectsPage onNavigate={switchView} />}
                {activeView === 'active-projects' && <MyActiveProjectsPage onNavigate={switchView} />}
                {activeView === 'my-proposals' && <MyProposalsPage onNavigate={switchView} />}
                {activeView === 'invitations' && <MyInvitationsPage />}
                {activeView === 'earnings' && <EarningsPage />}
                {activeView === 'create-project' && <ProjectCreatePage onNavigate={switchView} />}
                {activeView === 'project-detail' && selectedProjectId && <ProjectDetailPage projectId={selectedProjectId} onNavigate={switchView} onBack={goBack} />}
                {activeView === 'project-proposals' && selectedProjectId && <ProjectProposalsPage projectId={selectedProjectId} onNavigate={switchView} onBack={goBack} />}
                {activeView === 'project-workspace' && selectedProjectId && <ProjectWorkspacePage embeddedProjectId={selectedProjectId} onNavigate={switchView} onBack={goBack} />}
                {activeView === 'creator-detail' && selectedCreatorId && <CreatorDetailPage creatorId={selectedCreatorId} onNavigate={switchView} onBack={goBack} />}
              </div>
            </Suspense>
          )}

          {/* Home view content */}
          {isHomeView && (
          <>
          {/* Welcome */}
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-black text-[#0F172A] mb-1">
              {getGreeting()}, {displayName} 👋
            </h1>
            <p className="text-[#475569] text-sm md:text-base">
              Hier is je overzicht van vandaag
            </p>
          </div>

          {/* Profile Completion Banner */}
          <div className="mb-6">
            <ProfileCompletionBanner onNavigateToProfile={() => switchView('settings')} />
          </div>

          {/* Profile Picture Warning for Creators */}
          {user?.role === 'creator' && !user?.profilePictureUrl && (
            <div className="mb-6">
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-amber-800 mb-1">Profielfoto ontbreekt</h3>
                  <p className="text-sm text-amber-700 mb-3">Zonder profielfoto word je niet getoond op de zoekpagina.</p>
                  <button onClick={() => switchView('settings')} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 transition">
                    <User className="w-4 h-4" /> Profielfoto toevoegen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* BTW Warning for Companies */}
          {isCompany && !user?.btwNummer && (
            <div className="mb-6">
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-amber-800 mb-1">BTW informatie ontbreekt</h3>
                  <p className="text-sm text-amber-700">
                    Je account mist btw-informatie. Vul je btw-nummer in via{' '}
                    <button onClick={() => switchView('settings')} className="text-[#3EB489] font-semibold hover:underline">Instellingen</button>
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Tab Navigation (both companies and creators) */}
          <div id="dashboard-tabs" className="scroll-mt-20 flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 max-w-md">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'overview' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569] hover:text-[#0F172A]'
              }`}
            >
              Overzicht
            </button>
            <button
              onClick={() => setActiveTab('voorstellen')}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                activeTab === 'voorstellen' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569] hover:text-[#0F172A]'
              }`}
            >
              {isCompany ? 'Voorstellen' : 'Mijn Voorstellen'}
              {isCompany && stats.totalProposals > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === 'voorstellen' ? 'bg-[#3EB489] text-white' : 'bg-gray-200 text-gray-600'}`}>{stats.totalProposals}</span>
              )}
              {!isCompany && creatorProposals.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === 'voorstellen' ? 'bg-[#3EB489] text-white' : 'bg-gray-200 text-gray-600'}`}>{creatorProposals.length}</span>
              )}
            </button>
          </div>

          {/* === VOORSTELLEN TAB (CREATOR) === */}
          {activeTab === 'voorstellen' && !isCompany ? (
            <div>
              {creatorProposalsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-[#1D8BF8] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : creatorProposals.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-md">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">Nog geen voorstellen</h3>
                  <p className="text-[#475569] text-sm mb-6">Je hebt nog geen voorstellen ingediend op opdrachten</p>
                  <button onClick={() => switchView('projects')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3EB489] text-white rounded-xl text-sm font-semibold hover:bg-[#1a7de0] transition">
                    <Search className="w-4 h-4" /> Zoek Opdrachten
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Filter tabs */}
                  <div className="flex gap-2 mb-4 flex-wrap">
                    {[
                      { key: 'all', label: 'Alle', count: creatorProposals.length },
                      { key: 'submitted', label: 'Ingediend', count: creatorProposals.filter(p => p.status === 'submitted').length },
                      { key: 'accepted', label: 'Geaccepteerd', count: creatorProposals.filter(p => p.status === 'accepted').length },
                      { key: 'rejected', label: 'Afgewezen', count: creatorProposals.filter(p => p.status === 'rejected').length },
                    ].map(tab => {
                      const isActiveFilter = (activeTab === 'voorstellen' && expandedProjects[`filter_${tab.key}`] !== undefined ? expandedProjects[`filter_${tab.key}`] : tab.key === 'all')
                      return (
                        <button
                          key={tab.key}
                          onClick={() => {
                            const newExpanded = {}
                            newExpanded[`filter_${tab.key}`] = true
                            setExpandedProjects(newExpanded)
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                            isActiveFilter
                              ? 'bg-[#3EB489] text-white'
                              : 'bg-gray-100 text-[#475569] hover:bg-gray-200'
                          }`}
                        >
                          {tab.label} {tab.count > 0 && <span className="ml-1">({tab.count})</span>}
                        </button>
                      )
                    })}
                  </div>
                  {creatorProposals
                    .filter(p => {
                      const activeFilter = Object.keys(expandedProjects).find(k => k.startsWith('filter_') && expandedProjects[k])
                      const filterKey = activeFilter ? activeFilter.replace('filter_', '') : 'all'
                      return filterKey === 'all' || p.status === filterKey
                    })
                    .map((proposal, idx) => {
                      const proposalStatusColors = { submitted: 'bg-yellow-50 text-yellow-700 border-yellow-200', accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200', rejected: 'bg-red-50 text-red-600 border-red-200' }
                      const proposalStatusTexts = { submitted: 'Ingediend', accepted: 'Geaccepteerd', rejected: 'Afgewezen' }
                      const project = proposal.projectId || proposal.project
                      return (
                        <div key={proposal._id || proposal.id || idx} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-4 md:p-5">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-[#3EB489] rounded-xl flex items-center justify-center flex-shrink-0">
                              <Briefcase className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <h4 className="font-bold text-[#0F172A] text-sm md:text-base truncate">
                                    {project?.title || 'Opdracht verwijderd'}
                                  </h4>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${proposalStatusColors[proposal.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                      {proposalStatusTexts[proposal.status] || proposal.status}
                                    </span>
                                    {project?.owner?.companyName && (
                                      <span className="text-xs text-[#475569] flex items-center gap-1">
                                        <Users className="w-3 h-3" /> {project.owner.companyName}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  {(proposal.finalPrice || proposal.customPricing?.amount || proposal.proposedPrice) && (
                                    <div className="text-base font-bold text-[#0F172A]">€{(proposal.finalPrice || proposal.customPricing?.amount || proposal.proposedPrice || 0).toLocaleString('nl-NL')}</div>
                                  )}
                                  {proposal.deliveryTimeInDays && <div className="text-xs text-[#475569]">{proposal.deliveryTimeInDays} dagen</div>}
                                </div>
                              </div>
                              {proposal.motivation && <p className="text-sm text-[#475569] mt-2 line-clamp-2">{proposal.motivation}</p>}
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-3 gap-2">
                                <div className="text-xs text-[#94A3B8]">
                                  {(proposal.submittedAt || proposal.createdAt) ? new Date(proposal.submittedAt || proposal.createdAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  {project && (project._id || project.id) && (
                                    <button onClick={() => switchView('project-detail', project._id || project.id)} className="px-3 py-1.5 bg-emerald-50 text-[#3EB489] rounded-lg text-xs font-semibold hover:bg-emerald-100 transition flex items-center gap-1 whitespace-nowrap">
                                      <Eye className="w-3.5 h-3.5" /> Bekijk Opdracht
                                    </button>
                                  )}
                                  {proposal.status === 'accepted' && (proposal.childProject?._id || (project && (project._id || project.id))) && (
                                    <>
                                      {proposal.childProject && !proposal.childProject.depositPaid && (
                                        <span className="text-xs text-[#94A3B8] w-full sm:w-auto order-first sm:order-none">Wacht op aanbetaling van het bedrijf</span>
                                      )}
                                      {/* Meerdere creators per opdracht: de workspace is het eigen kindproject; legacy = de opdracht zelf */}
                                      <button onClick={() => switchView('project-workspace', proposal.childProject?._id || project._id || project.id)} className="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition whitespace-nowrap">
                                        Workspace
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          ) : activeTab === 'voorstellen' && isCompany ? (
            <div>
              {proposalsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-[#3EB489] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : projectsWithProposals.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-md">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">Nog geen voorstellen</h3>
                  <p className="text-[#475569] text-sm mb-6">Je hebt nog geen voorstellen ontvangen op je opdrachten</p>
                  <button onClick={() => switchView('create-project')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3EB489] text-white rounded-xl text-sm font-semibold hover:bg-[#359E78] transition">
                    <Plus className="w-4 h-4" /> Nieuwe Opdracht Plaatsen
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {projectsWithProposals.map(project => {
                    const projectId = project._id || project.id
                    const isExpanded = expandedProjects[projectId] !== false
                    return (
                      <div key={projectId} className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
                        <div role="button" tabIndex={0} aria-expanded={isExpanded} onClick={() => toggleProject(projectId)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleProject(projectId) } }} className="w-full px-4 md:px-6 py-4 flex items-center justify-between gap-3 hover:bg-gray-50 transition cursor-pointer">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-[#3EB489] rounded-xl flex items-center justify-center flex-shrink-0">
                              <Briefcase className="w-5 h-5 text-white" />
                            </div>
                            <div className="text-left min-w-0">
                              <h3 className="font-bold text-[#0F172A] text-sm md:text-base truncate">{project.title}</h3>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>{getStatusText(project.status)}</span>
                                <span className="text-xs text-[#475569]">{project.proposals.length} voorstel{project.proposals.length !== 1 ? 'len' : ''}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); switchView('project-proposals', projectId) }} className="flex items-center gap-1 p-2.5 md:px-3 md:py-1.5 text-xs font-medium text-[#3EB489] bg-emerald-50 hover:bg-emerald-100 rounded-lg transition" aria-label="Bekijk alle voorstellen" title="Bekijk alle voorstellen">
                              <ExternalLink className="w-4 h-4 md:w-3.5 md:h-3.5" /> <span className="hidden md:inline">Bekijk alle voorstellen</span>
                            </button>
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-gray-100">
                            {project.proposals.map((proposal, idx) => {
                              const proposalStatusColors = { submitted: 'bg-yellow-50 text-yellow-700 border-yellow-200', accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200', rejected: 'bg-red-50 text-red-600 border-red-200' }
                              const proposalStatusTexts = { submitted: 'Nieuw', accepted: 'Geaccepteerd', rejected: 'Afgewezen' }
                              return (
                                <div key={proposal.id || idx} className={`px-4 md:px-6 py-4 ${idx !== project.proposals.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-[#F8FAFC] transition`}>
                                  <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                      {proposal.creator?.profilePictureUrl ? (
                                        <img src={proposal.creator.profilePictureUrl} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <User className="w-5 h-5 text-gray-400" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="font-semibold text-[#0F172A] text-sm truncate">{proposal.creator?.name || 'Onbekende creator'}</h4>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${proposalStatusColors[proposal.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{proposalStatusTexts[proposal.status] || proposal.status}</span>
                                          </div>
                                          {proposal.creator?.niches?.length > 0 && (
                                            <div className="flex gap-1 mt-1 flex-wrap">
                                              {proposal.creator.niches.slice(0, 3).map((niche, i) => (
                                                <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px]">{niche}</span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                          {(proposal.finalPrice || proposal.customPricing?.amount) && (
                                            <div className="text-base font-bold text-[#0F172A]">€{(proposal.finalPrice || proposal.customPricing?.amount || 0).toLocaleString('nl-NL')}</div>
                                          )}
                                          {proposal.deliveryTimeInDays && <div className="text-xs text-[#475569]">{proposal.deliveryTimeInDays} dagen</div>}
                                        </div>
                                      </div>
                                      {proposal.motivation && <p className="text-sm text-[#475569] mt-2 line-clamp-2">{proposal.motivation}</p>}
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-3 gap-2">
                                        <div className="text-xs text-[#94A3B8]">
                                          {proposal.submittedAt ? new Date(proposal.submittedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : ''}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                          {proposal.status === 'submitted' && (
                                            <>
                                              <button onClick={() => handleAcceptAndPay(proposal, project)} className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-semibold hover:bg-emerald-600 transition whitespace-nowrap">Accepteren & aanbetalen</button>
                                              <button onClick={() => handleRejectProposal(proposal.id)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-red-50 hover:text-red-600 transition whitespace-nowrap">Afwijzen</button>
                                            </>
                                          )}
                                          {proposal.status === 'accepted' && (() => {
                                            // Meerdere creators per opdracht: elk geaccepteerd voorstel heeft een eigen kindproject
                                            const childId = proposal.childProject?.id || proposal.childProject?._id
                                            const workspaceId = childId || (project.selectedCreator ? (project._id || project.id) : null)
                                            const depositPaid = childId ? proposal.childProject?.payment?.deposit?.status === 'paid' : project.payment?.deposit?.status === 'paid'
                                            if (!workspaceId) return null
                                            return depositPaid ? (
                                              <button onClick={() => switchView('project-workspace', workspaceId)} className="px-3 py-1.5 border border-[#3EB489] text-[#3EB489] rounded-lg text-xs font-semibold hover:bg-emerald-50 transition whitespace-nowrap">Workspace</button>
                                            ) : (
                                              <button onClick={() => navigate(`/projects/${workspaceId}/payment`)} className="px-3 py-1.5 bg-[#3EB489] text-white rounded-lg text-xs font-semibold hover:bg-[#359E78] transition whitespace-nowrap">Aanbetaling betalen</button>
                                            )
                                          })()}
                                          <button onClick={() => switchView('creator-detail', proposal.creator?.id)} className="px-3 py-1.5 bg-emerald-50 text-[#3EB489] rounded-lg text-xs font-semibold hover:bg-emerald-100 transition whitespace-nowrap">Profiel</button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8" data-tour="dashboard-stats">
              {isCompany ? (
                <>
                  <button onClick={() => switchView('my-projects')} className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-[#3EB489]/30 transition-all text-left w-full">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center"><Briefcase className="w-5 h-5 text-[#3EB489]" /></div>
                      <span className="text-2xl font-bold text-[#0F172A]">{stats.totalProjects || 0}</span>
                    </div>
                    <p className="text-xs font-medium text-[#475569]">Totaal Opdrachten</p>
                  </button>
                  <div className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-yellow-50 rounded-xl flex items-center justify-center"><Clock className="w-5 h-5 text-yellow-500" /></div>
                      <span className="text-2xl font-bold text-[#0F172A]">{stats.activeProjects || 0}</span>
                    </div>
                    <p className="text-xs font-medium text-[#475569]">Actieve Opdrachten</p>
                  </div>
                  <div className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-500" /></div>
                      <span className="text-2xl font-bold text-[#0F172A]">{stats.completedProjects || 0}</span>
                    </div>
                    <p className="text-xs font-medium text-[#475569]">Voltooid</p>
                  </div>
                  <div className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center"><Users className="w-5 h-5 text-[#7c3aed]" /></div>
                      <span className="text-2xl font-bold text-[#0F172A]">{stats.totalProposals || 0}</span>
                    </div>
                    <p className="text-xs font-medium text-[#475569]">Reacties</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center"><MessageSquare className="w-5 h-5 text-[#7c3aed]" /></div>
                      <span className="text-2xl font-bold text-[#0F172A]">{stats.totalProposals || 0}</span>
                    </div>
                    <p className="text-xs font-medium text-[#475569]">Reacties</p>
                  </div>
                  <button onClick={() => switchView('active-projects')} className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all text-left w-full">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-500" /></div>
                      <span className="text-2xl font-bold text-[#0F172A]">{stats.acceptedProposals || 0}</span>
                    </div>
                    <p className="text-xs font-medium text-[#475569]">Geaccepteerd</p>
                  </button>
                  <div className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center"><Star className="w-5 h-5 text-[#3EB489]" /></div>
                      <span className="text-2xl font-bold text-[#0F172A]">{stats.completedProjects || 0}</span>
                    </div>
                    <p className="text-xs font-medium text-[#475569]">Voltooid</p>
                  </div>
                  <div className="p-4 md:p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-yellow-50 rounded-xl flex items-center justify-center"><Euro className="w-5 h-5 text-yellow-500" /></div>
                      <span className="text-xl md:text-2xl font-bold text-[#0F172A] truncate ml-2">{Number(stats.totalEarnings || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}</span>
                    </div>
                    <p className="text-xs font-medium text-[#475569]">Verdiend</p>
                  </div>
                </>
              )}
            </div>

            {/* Creators Section removed - accessible via sidebar */}

            {/* Zo werkt het — uitleg van de flow voor bedrijven */}
            {isCompany && (
              <div className="mb-8 rounded-2xl border border-[#E5E7EB] bg-white p-4 sm:p-5 md:p-6 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_10px_30px_-18px_rgba(0,0,0,0.10)]">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div>
                    <h2 className="text-lg md:text-xl font-bold text-[#0A0F0D]">Zo werkt het</h2>
                    <p className="text-sm text-[#64748B]">Van opdracht plaatsen tot samenwerken met je creator — in 5 stappen.</p>
                  </div>
                  <button
                    onClick={() => switchView('create-project')}
                    className="inline-flex items-center px-4 py-2 bg-[#3EB489] rounded-xl text-sm font-semibold text-white hover:bg-[#359E78] hover:-translate-y-0.5 transition-all shadow-[0_4px_14px_-3px_rgba(62,180,137,0.5)]"
                  >
                    Nieuwe opdracht plaatsen
                  </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
                  {[
                    { t: 'Plaats je opdracht', d: 'Gratis en in een paar minuten klaar.' },
                    { t: 'Ontvang voorstellen', d: 'Creators reageren met prijs & motivatie.' },
                    { t: 'Kies & betaal 10%', d: 'De aanbetaling opent de workspace.' },
                    { t: 'Werk samen', d: 'Brief, chat en volg de content in de workspace.' },
                    { t: 'Keur goed & betaal 90%', d: 'De content is daarna helemaal van jou.' },
                  ].map((step, i) => (
                    <div key={i} className="rounded-xl bg-white border border-[#E5E7EB] p-3 sm:p-3.5">
                      <div className="w-7 h-7 rounded-full bg-[#ECFDF5] text-[#2A7E5F] border border-[#D1FAE5] flex items-center justify-center text-xs font-bold mb-2">
                        {i + 1}
                      </div>
                      <p className="text-sm font-semibold text-[#0A0F0D] leading-snug mb-0.5">{step.t}</p>
                      <p className="text-xs text-[#64748B] leading-relaxed">{step.d}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="mb-8">
              <h2 className="text-lg md:text-xl font-bold text-[#0F172A] mb-4">Snelle Acties</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {isCompany ? (
                  <>
                    <button onClick={() => switchView('my-projects')} className="group text-left" data-tour="action-my-projects">
                      <div className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-[#7c3aed]/30 transition-all flex items-center gap-3">
                        <div className="w-11 h-11 bg-[#7c3aed] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-md"><Briefcase className="w-5 h-5 text-white" /></div>
                        <div><h3 className="font-bold text-sm text-[#0F172A]">Mijn Opdrachten</h3><p className="text-xs text-[#475569]">Bekijk al je opdrachten</p></div>
                      </div>
                    </button>
                    <button onClick={() => switchView('create-project')} className="group text-left" data-tour="action-create-project">
                      <div className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-[#3EB489]/30 transition-all flex items-center gap-3">
                        <div className="w-11 h-11 bg-[#3EB489] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-md"><Plus className="w-5 h-5 text-white" /></div>
                        <div><h3 className="font-bold text-sm text-[#0F172A]">Nieuwe Opdracht</h3><p className="text-xs text-[#475569]">Plaats een content opdracht</p></div>
                      </div>
                    </button>
                    <button onClick={() => switchView('analytics')} className="group text-left">
                      <div className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all flex items-center gap-3">
                        <div className="w-11 h-11 bg-emerald-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-md"><TrendingUp className="w-5 h-5 text-white" /></div>
                        <div><h3 className="font-bold text-sm text-[#0F172A]">Analytics</h3><p className="text-xs text-[#475569]">Bekijk je statistieken</p></div>
                      </div>
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => switchView('projects')} className="group text-left" data-tour="action-projects">
                      <div className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-[#3EB489]/30 transition-all flex items-center gap-3">
                        <div className="w-11 h-11 bg-[#3EB489] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-md"><Search className="w-5 h-5 text-white" /></div>
                        <div><h3 className="font-bold text-sm text-[#0F172A]">Zoek Opdrachten</h3><p className="text-xs text-[#475569]">Vind nieuwe projecten</p></div>
                      </div>
                    </button>
                    <button onClick={() => switchView('settings')} className="group text-left" data-tour="action-profile">
                      <div className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-[#3EB489]/30 transition-all flex items-center gap-3">
                        <div className="w-11 h-11 bg-[#3EB489] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-md"><User className="w-5 h-5 text-white" /></div>
                        <div><h3 className="font-bold text-sm text-[#0F172A]">Profiel Bijwerken</h3><p className="text-xs text-[#475569]">Verbeter je portfolio</p></div>
                      </div>
                    </button>
                    <button onClick={() => switchView('earnings')} className="group text-left">
                      <div className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all flex items-center gap-3">
                        <div className="w-11 h-11 bg-emerald-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-md"><TrendingUp className="w-5 h-5 text-white" /></div>
                        <div><h3 className="font-bold text-sm text-[#0F172A]">Inkomsten</h3><p className="text-xs text-[#475569]">Bekijk je verdiensten</p></div>
                      </div>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-[#0F172A]">
                  {isCompany ? 'Mijn Opdrachten' : 'Recente Activiteit'}
                  {recentActivity.length > 0 && <span className="ml-2 text-sm font-normal text-[#475569]">{recentActivity.length} resultaten</span>}
                </h3>
                {isCompany && (
                  <button onClick={() => switchView('my-projects')} className="text-sm font-semibold text-[#3EB489] hover:underline">Alle orders</button>
                )}
              </div>
              <div className="p-4">
                {recentActivity.length === 0 ? (
                  <div className="text-center py-10">
                    <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-[#475569] text-sm">
                      {isCompany ? 'Je hebt nog geen opdrachten! Zodra je jouw eerste opdracht hebt gestart, vind je hier een overzicht.' : 'Nog geen recente activiteit'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentActivity.map((activity, index) => (
                      <div key={index} className="p-3 rounded-xl bg-[#F8FAFC] border border-gray-100 hover:border-gray-200 transition">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 bg-[#3EB489] rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                            {activity.type === 'project' ? <Briefcase className="w-4 h-4 text-white" /> : <MessageSquare className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-semibold text-[#0F172A] text-sm truncate">{activity.title}</h4>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${getStatusColor(activity.status)}`}>{getStatusText(activity.status)}</span>
                            </div>
                            <p className="text-xs text-[#475569] mt-1">
                              {activity.date && !isNaN(new Date(activity.date)) ? new Date(activity.date).toLocaleDateString('nl-NL') : ''}
                              {activity.data?.proposalCount > 0 && <span className="ml-2 text-[#3EB489]">{activity.data.proposalCount} voorstellen</span>}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              {isCompany && activity.type === 'project' && activity.data?.proposalCount > 0 && (
                                <button onClick={() => switchView('project-proposals', activity.data._id || activity.data.id)} className="px-3 py-1 bg-emerald-50 text-[#3EB489] hover:bg-emerald-100 rounded-lg text-xs font-medium transition">Bekijk Voorstellen</button>
                              )}
                              {user?.role === 'creator' && activity.status === 'accepted' && activity.data?.projectId && (
                                <button onClick={() => switchView('project-detail', activity.data.projectId._id || activity.data.projectId)} className="px-3 py-1 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg text-xs font-medium transition">Bekijk Opdracht</button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
          )}
          </>
          )}
        </div>
      </main>
    </div>
  )
}

export default DarkDashboardPage
